/* sb-dc-tutvideo.js - "Step & Repeat Video" button in the design center header.
   2026-10-06.

   Sits right next to "See All Design Templates" (#sbdc-tpl-btn, created by
   sb-dc-tplbutton.js) in the top app bar, styled as the store's orange pill so
   it reads as a call to action. Clicking it opens a lightweight lightbox with
   the Step & Repeat video, and the same instructions the product page carries
   above the clip.

   2026-10-09 (theme "DC la"): the lightbox shows the video ONLY (store's
   request) - the instructions panel above the clip is no longer added.
   buildText() and the wording are kept below, unused, so putting the panel
   back is one line in buildOverlay(). Same change in sb-dc-tplvideo.js and
   snippets/sb-dc-cta.liquid.

   Only on step & repeat products - see wanted() below.

   Why a lightbox and not the app itself: the editor's own content is driven
   through Supabase, and the tutorial clip must not wait on that. The clip is
   a plain file on Shopify's CDN, so the overlay only sets the <video> src on
   click (preload="none" until then) and streams straight from the CDN -
   nothing about it touches Supabase, and nothing downloads until the customer
   asks for it.

   The header is React's and repaints, so the button is re-inserted on a timer
   (same approach as sb-dc-tplbutton.js). The overlay lives on <body>, outside
   #root, so a React repaint never disturbs it. If the script ever errors, the
   button's own href still opens the clip in a new tab.

   Same clip as the product-page card (snippets/sb-dc-cta.liquid) and the
   templates-page button (assets/sb-dc-tplvideo.js) - change the URL in all
   three if it is ever replaced. */
(function () {
  'use strict';

  var BTN_ID = 'sbdc-tutvideo-btn';
  var REF_ID = 'sbdc-tpl-btn'; /* the "See All Design Templates" button */
  var OV_ID = 'sbdc-tutvideo-overlay';
  var VID_ID = 'sbdc-tutvideo-el';
  var FB_ID = 'sbdc-tutvideo-fallback';
  var LABEL = 'Step & Repeat Video';
  /* 2026-10-07: clip replaced (was 0407d58e...). The store's link ended ".mp4f";
     that 404s, ".mp4" is the file - 1920x1080, 33s. */
  var VIDEO_URL = 'https://cdn.shopify.com/videos/c/o/v/a855d7eb1015432d837f25563c657ab7.mp4';

  /* 2026-10-06: the same lines the product page carries with its copy of this
     clip (snippets/sb-dc-cta.liquid) and the templates page
     (assets/sb-dc-tplvideo.js). Kept word for word on purpose - a customer who
     read them in one place should not meet a reworded version in another.
     If the wording changes, change it in all three.
     2026-10-09: not shown any more (video only) - see the header note. */
  var CAP = 'Design it yourself in minutes';
  var HEAD = 'Making your own Step & Repeat is this easy';
  var BULLETS = [
    'Drop your logo into a ready-made pattern and watch it fill the entire ' +
      'backdrop instantly - no placing logos one by one.',
    'Slide the logos bigger, smaller, closer together, further apart. It is your call.',
    'Every change shows up live on screen, exactly as it will print.',
    'Free to use. No design skills, no extra charge.'
  ];

  /* ---- overlay -------------------------------------------------------- */

  function buildOverlay() {
    var ov = document.getElementById(OV_ID);
    if (ov) return ov;

    ov = document.createElement('div');
    ov.id = OV_ID;
    ov.setAttribute('role', 'dialog');
    ov.setAttribute('aria-modal', 'true');
    ov.setAttribute('aria-label', 'How to use Step and Repeat');
    ov.style.cssText = 'position:fixed;inset:0;z-index:2147483000;display:none;' +
      'align-items:center;justify-content:center;background:rgba(0,0,0,.82);padding:24px';

    var box = document.createElement('div');
    box.style.cssText = 'position:relative;width:min(920px,92vw);max-height:92vh;overflow:auto;' +
      'background:#000;border-radius:10px;box-shadow:0 20px 60px rgba(0,0,0,.5)';

    var close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '&times;';
    close.style.cssText = 'position:absolute;top:8px;right:10px;z-index:2;width:36px;height:36px;' +
      'border:0;border-radius:50%;background:rgba(0,0,0,.55);color:#fff;font-size:24px;' +
      'line-height:36px;cursor:pointer';
    close.addEventListener('click', hide);

    var video = document.createElement('video');
    video.id = VID_ID;
    video.controls = true;
    video.playsInline = true;
    video.preload = 'none';
    /* 2026-10-09: 84vh - with the text panel gone the clip has the box to itself. */
    video.style.cssText = 'display:block;width:100%;height:auto;max-height:84vh;background:#000';

    var fb = document.createElement('div');
    fb.id = FB_ID;
    fb.style.cssText = 'display:none;padding:18px;color:#fff;font:14px/1.5 Arial,sans-serif;text-align:center';
    fb.innerHTML = 'This browser cannot play the video inline. ' +
      '<a href="' + VIDEO_URL + '" target="_blank" rel="noopener" style="color:#7fbfff">Open it in a new tab</a>.';

    video.addEventListener('error', function () { fb.style.display = 'block'; });

    box.appendChild(close);
    /* 2026-10-09: video only - box.appendChild(buildText()) removed. */
    box.appendChild(video);
    box.appendChild(fb);
    ov.appendChild(box);

    ov.addEventListener('click', function (e) { if (e.target === ov) hide(); });

    document.body.appendChild(ov);
    return ov;
  }

  /* The instructions, ABOVE the clip and inside the same rounded box.
     Above rather than below (2026-10-06, the store's call) so the order matches
     the product-page box, which reads instructions first and then plays.
     The box scrolls if a short window cannot hold text + clip; the clip's own
     max-height is lowered to leave room for them.

     2026-10-07: dark, not white (store's request). The panel sits directly on
     top of the clip inside one rounded box, and a white slab above a black
     video cut the box in two - black runs straight into the clip instead. The
     words turn white and the green of the label and heading turns the store's
     orange, which holds up on black where #1B6543 does not.

     2026-10-09: unused (video only). */
  function buildText() {
    var wrap = document.createElement('div');
    /* Padding-right clears the close button, which is pinned to the top right
       of the box and now sits over this panel rather than over the clip. */
    wrap.style.cssText = 'background:#000;padding:16px 56px 16px 20px;' +
      'font:400 13px/1.45 Arial,Helvetica,sans-serif;color:#fff';

    var cap = document.createElement('div');
    cap.style.cssText = 'display:flex;align-items:center;gap:6px;margin:0 0 6px;' +
      'font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;' +
      'color:#FD913E;line-height:1.3';
    var tri = document.createElement('span');
    tri.setAttribute('aria-hidden', 'true');
    tri.style.cssText = 'display:inline-block;width:0;height:0;border-style:solid;' +
      'border-width:4px 0 4px 7px;border-color:transparent transparent transparent #FD913E';
    cap.appendChild(tri);
    cap.appendChild(document.createTextNode(CAP));

    var h = document.createElement('b');
    h.textContent = HEAD;
    /* 2026-10-07: not bold (store's request) - it is a <b>, so the weight is
       set back to normal explicitly. */
    h.style.cssText = 'display:block;font-size:13.5px;font-weight:400;line-height:1.3;color:#FD913E';

    var ul = document.createElement('ul');
    ul.style.cssText = 'margin:6px 0 0;padding:0 0 0 17px;list-style:disc';
    for (var i = 0; i < BULLETS.length; i++) {
      var li = document.createElement('li');
      li.textContent = BULLETS[i];
      li.style.cssText = 'margin:0 0 4px';
      ul.appendChild(li);
    }

    wrap.appendChild(cap);
    wrap.appendChild(h);
    wrap.appendChild(ul);
    return wrap;
  }

  function onKey(e) { if (e.key === 'Escape' || e.keyCode === 27) hide(); }

  function show() {
    var ov = buildOverlay();
    var video = document.getElementById(VID_ID);
    var fb = document.getElementById(FB_ID);
    if (fb) fb.style.display = 'none';
    ov.style.display = 'flex';
    document.addEventListener('keydown', onKey);
    /* Set src now (not before) so nothing loads until the customer clicks. */
    if (video && video.getAttribute('src') !== VIDEO_URL) video.setAttribute('src', VIDEO_URL);
    if (video) { try { var p = video.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {} }
  }

  function hide() {
    var ov = document.getElementById(OV_ID);
    if (!ov) return;
    ov.style.display = 'none';
    document.removeEventListener('keydown', onKey);
    var video = document.getElementById(VID_ID);
    if (video) {
      try { video.pause(); } catch (e) {}
      /* Drop the source so the CDN stream stops in the background. */
      video.removeAttribute('src');
      try { video.load(); } catch (e) {}
    }
  }

  /* ---- which products get the button ---------------------------------- */

  /* 2026-10-06: the button was showing on every product - a teardrop flag was
     being offered a step & repeat video. The editor's own state is the source
     of truth here, not the URL: the customer can switch product inside the
     editor without the address changing, and __dcSelectedProduct follows that.
     The URL's ?product= is only the fallback for the moment before the app has
     set it.
     Same families as the product-page gate in snippets/sb-dc-cta.liquid -
     keep the two in step. Pop-up display BACKDROPS count as step & repeat
     (store's call); 'pop-up-display-stand' is hardware and deliberately misses,
     which is why this matches on the longer word. */
  function handle() {
    try {
      var p = window.__dcSelectedProduct;
      if (p && p.handle) return String(p.handle).toLowerCase();
      if (typeof window.__dcAppliedProduct === 'function') {
        var q = window.__dcAppliedProduct();
        if (q && q.handle) return String(q.handle).toLowerCase();
      }
    } catch (e) {}
    try {
      var u = new URLSearchParams(window.location.search).get('product');
      if (u) return String(u).toLowerCase();
    } catch (e) {}
    return '';
  }

  function wanted() {
    var h = handle();
    if (!h) return false;                                   /* unknown - stay off */
    if (h.indexOf('step') > -1 && h.indexOf('repeat') > -1) return true;
    if (h === 'repeat-banners') return true;
    if (h.indexOf('checkerboard') > -1 || h.indexOf('logo-wall') > -1) return true;
    if (h.indexOf('pop-up-display-backdrop') > -1) return true;
    return false;
  }

  /* ---- button --------------------------------------------------------- */

  function place() {
    var ref = document.getElementById(REF_ID);
    if (!ref || !ref.parentNode) return; /* wait for sb-dc-tplbutton.js */

    var a = document.getElementById(BTN_ID);

    /* Not a step & repeat product: take the button away again if the customer
       switched to one of these from one that does carry it, and close the
       lightbox with it. */
    if (!wanted()) {
      if (a && a.parentNode) a.parentNode.removeChild(a);
      var open = document.getElementById(OV_ID);
      if (open && open.style.display === 'flex') hide();
      return;
    }

    if (!a) {
      a = document.createElement('a');
      a.id = BTN_ID;
      a.href = VIDEO_URL;            /* real fallback if the script errors */
      a.target = '_blank';
      a.rel = 'noopener';
      a.className = ref.className;    /* inherit the header's font/size */
      /* 2026-10-06: the store's orange behind the header's own navy text.
         The orange is taken from the site's buttons rather than picked by eye -
         Add to Cart and "See All locations" both compute to rgb(253,145,62).
         White text was tried first and the store preferred the navy it already
         had, so only the fill changed. The play mark follows currentColor,
         so it is navy too. */
      a.style.textDecoration = 'none';
      a.style.display = 'inline-flex';
      a.style.alignItems = 'center';
      a.style.background = '#FAD89D';   /* 2026-10-08: same warm yellow as the picked SR «Number of logos» tab (owner) */
      a.style.color = '#072747';
      a.style.border = '1px solid rgba(0,0,0,.06)';
      a.style.borderRadius = '6px';
      a.style.padding = '6px 12px';
      a.style.fontWeight = '700';
      a.style.boxShadow = '0 1px 3px rgba(0,0,0,.18)';

      /* 2026-10-07: the play mark sits in a ring, same as the templates-page
         button (assets/sb-dc-tplvideo.js) and the product-page link - the store
         asked for the three to match. An SVG rather than the CSS-border
         triangle it replaced: a circle around it needs a real shape. Both parts
         follow currentColor, so they stay with the button's navy. */
      var mark = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      mark.setAttribute('viewBox', '0 0 24 24');
      mark.setAttribute('width', '16');
      mark.setAttribute('height', '16');
      mark.setAttribute('aria-hidden', 'true');
      mark.style.cssText = 'flex:0 0 auto;display:block;margin-right:7px';
      mark.innerHTML =
        '<circle cx="12" cy="12" r="9.25" fill="none" stroke="currentColor" stroke-width="1.9"></circle>' +
        '<path d="M10 8.2l6 3.8-6 3.8z" fill="currentColor"></path>';
      a.appendChild(mark);
      a.appendChild(document.createTextNode(LABEL));

      a.addEventListener('click', function (e) {
        try { e.preventDefault(); show(); }
        catch (err) { /* let the href open the clip in a new tab */ }
      });
    }

    /* Keep it right after "See All Design Templates" through header repaints. */
    if (ref.nextSibling !== a) ref.parentNode.insertBefore(a, ref.nextSibling);
  }

  var t = setInterval(function () { try { place(); } catch (e) {} }, 400);
  window.addEventListener('pagehide', function () { clearInterval(t); });
})();
